import { ENTITY_TYPES, type EntityType } from './entity-types';
import type { ActiveMembership } from '@/lib/types/auth.types';

export const ROUTES = {
  HOME: '/',
  AUTH: {
    LOGIN: '/login',
    // Step 2 of login (FR-04). Reachable ONLY as a transition from LOGIN: it
    // fails the ADR FE-1 "can they arrive cold?" test, so it carries no
    // shareable state and redirects to LOGIN without a pre-auth credential.
    TWO_FACTOR: '/login/verify',
    // Authenticator-app setup for a login with no app yet (MYN-241). Same
    // transition-only rule as TWO_FACTOR: no pending login, back to LOGIN.
    TWO_FACTOR_SETUP: '/login/setup-2fa',
    LOGOUT: '/logout',
    REGISTER: '/register',
    VERIFY_EMAIL: '/verify-email',
    EMAIL_VERIFIED: '/email-verified',
    FORGOT_PASSWORD: '/forgot-password',
    // Invite-accept signup (US-038/MYN-47). Also renders the expired/invalid state.
    INVITE_SIGNUP: '/invite/:token',
    // Step 2 of invite-accept (US-098): email-OTP verification.
    INVITE_VERIFY_OTP: '/invite/:token/verify-otp',
  },
  DASHBOARD: '/dashboard',
  /** A view-as tab's entry (`/view-as#<token>`) and, without a token, its "session ended" page (MYN-335). */
  VIEW_AS: '/view-as',
  // Individual person account (FR-09), not an entity profile.
  ACCOUNT: '/me',
  // Owned entity workspace pages - persona-prefixed (ADR FE-1).
  STARTUP_PROFILE: '/startup/profile',
  /** The founder's preview of the profile as a share-link recipient sees it. */
  STARTUP_PROFILE_PREVIEW: '/startup/profile/preview',
  // Create-startup flow (MYN-46).
  CREATE_STARTUP: '/startup/create',
  STARTUP_CREATED: '/startup/created',
  // Team Access (MYN-43) - owned workspace page, startup-admin only.
  STARTUP_TEAM: '/startup/team',
  INVESTOR_PROFILE: '/investor/profile',
  // Team Access for the investor persona - investor-admin/member only.
  INVESTOR_TEAM: '/investor/team',
  PARTNER_PROFILE: '/partner/profile',
  // Team Access for the trusted partner persona (MYN-102/104) - reuses the
  // same TeamPermissionsPage as STARTUP_TEAM/INVESTOR_TEAM (TEAM_PAGE_FEATURES
  // already has a trusted_partner entry).
  PARTNER_TEAM: '/partner/team',
  // Trusted Partner's own offer-management console ("Perks" in the nav) -
  // FR-29, MYN-104. Distinct from the member-facing offers section on the
  // Trusted Partners page (FR-30), which every persona sees (A-90).
  PARTNER_OFFERS: '/partner/offers',
  PARTNER_OFFER_NEW: '/partner/offers/new',
  PARTNER_OFFER_EDIT: '/partner/offers/:offerId/edit',
  // Public marketing page (Task 3 built the component; Extra 4 wires the route).
  BENEFITS: '/benefits',
  // Public marketing pages not yet built (MYN-211 CTA-routing fix) - render a
  // "coming soon" placeholder under PublicLayout until their own build ticket
  // ships (Due Diligence Platform: parked per B-17; Investors: MYN-255; About
  // was never its own ticket, but Home's Mission band CTA already points here).
  ABOUT: '/about',
  // Renamed from "/due-diligence-platform" (MYN-326 follow-up, System
  // Designer instruction after seeing the renamed page live - supersedes
  // A-338, which kept the route unchanged). The key keeps its old name
  // deliberately, matching every other internal identifier this rename left
  // alone (the CMS slug, the page component's file name) - only the
  // user-facing label and the URL changed. `DUE_DILIGENCE_PLATFORM_LEGACY`
  // below is the old path, kept only as a redirect source.
  DUE_DILIGENCE_PLATFORM: '/fundraising-support',
  DUE_DILIGENCE_PLATFORM_LEGACY: '/due-diligence-platform',
  INVESTORS: '/investors',
  // Public Trusted Partners marketing page (MYN-216/MYN-215). Deliberately
  // NOT `/trusted-partners` - that path is already `TRUSTED_PARTNERS` below,
  // the unrelated authenticated in-app Partner Directory + Perks page
  // (FR-111, MYN-107/108). A-126.
  PARTNERS: '/partners',
  // Shared in-app pages.
  EVENTS: '/events',
  // Event detail page (MYN-51) and private-event direct-link route (MYN-93).
  // Flat `/e/` prefix so `robots.txt` carries one rule (`Disallow: /e/`)
  // instead of enumerating individual event UUIDs.
  EVENT_DETAIL: '/e/:id',
  RESOURCES: '/resources',
  // Resource folder detail page — slug-based URL (MYN-53).
  RESOURCE_FOLDER: '/resources/folders/:slug',
  // Admin console: Resources section. One sidebar entry with Items,
  // Folders and Overview as path tabs beneath it; the bare path redirects to Items, the
  // default tab, so the sidebar link never needs to know which tab is first.
  ADMIN_RESOURCES: '/admin/resources',
  // Admin console: resource folder management (MYN-53).
  ADMIN_RESOURCES_FOLDERS: '/admin/resources/folders',
  // Admin console: resource item management (MYN-100).
  ADMIN_RESOURCES_ITEMS: '/admin/resources/items',
  // Admin console: resource statistics overview (MYN-194).
  ADMIN_RESOURCES_OVERVIEW: '/admin/resources/overview',
  TRUSTED_PARTNERS: '/trusted-partners',
  // Billing is a TOP-LEVEL item, not a section of the account area - settled by
  // the 2026-09-22 designs ("Billing & Info" in the nav) and FR-41's AC was
  // corrected to match. Startup Admin only: a non-admin member does not see the
  // route, and a direct visit is refused rather than rendered read-only.
  BILLING: '/billing',
  // The pre-checkout confirmation (FR-39, MYN-111). Its own route rather than a
  // modal on BILLING, because a free-tier startup has no billing state to show
  // behind it and the nav CTA links straight here.
  BILLING_PURCHASE: '/billing/purchase',
  // The review step between choosing a plan and Stripe (Sprint 7 QA): seats,
  // promo code and the total due. The plan is in `?plan=`, so a refresh keeps it.
  BILLING_REVIEW: '/billing/purchase/review',
  // Where Stripe sends the browser back. THIS PATH IS A CROSS-REPO CONTRACT:
  // it must match the Backend's `STRIPE_CHECKOUT_RETURN_PATH` (default
  // `/billing/return`), which is baked into the Stripe session's success and
  // cancel URLs. Changing it here alone sends every returning buyer to the 404
  // page. Reachable cold by construction - the member has been off-site - so
  // it reads its outcome from the backend rather than from router state (A-73).
  BILLING_CHECKOUT_RETURN: '/billing/return',
  // Full paginated partner list (FR-111 "View All Partners"). Static segment, so it
  // never collides with the `:entityId` profile route below.
  TRUSTED_PARTNERS_DIRECTORY: '/trusted-partners/directory',
  // Startup Perks page (MYN-252): every live offer, under Trusted Partners like
  // the directory. Static segment, so it also outranks `:entityId`.
  TRUSTED_PARTNERS_PERKS: '/trusted-partners/perks',
  TRUSTED_PARTNER_DETAIL: '/trusted-partners/:entityId',
  DATA_ROOM: '/data-room',
  // The Issue Report CTA journey (FR-204, MYN-286): one public route that
  // decides the next step for the visitor's account. Public layout, because it
  // must work logged out; flat and resource-first (ADR FE-1 "can they arrive
  // cold?": yes - it is where the public Due Diligence page's CTA points).
  ISSUE_REPORT_START: '/issue-report/start',
  // Data-room SSO handoff interstitial (MYN-164). Reachable ONLY as a
  // transition from a launch affordance, which passes the acting startup in
  // the router's location state: like AUTH.TWO_FACTOR it fails the ADR FE-1
  // "can they arrive cold?" test, so arriving without that state redirects to
  // the dashboard rather than launching anything.
  DATA_ROOM_HANDOFF: '/data-room/opening',
  // Super Admin console.
  ADMIN: '/admin',
  // Super Admin console: Audit Log viewer (MYN-135).
  ADMIN_AUDIT: '/admin/audit',
  // Super Admin console: Events (MYN-92 / MYN-97 / MYN-98).
  ADMIN_EVENTS: '/admin/events',
  // Full-page event create and edit routes. Declared here (Task 1's contract)
  // and REGISTERED by Task 2 alongside `EventFormPage` - nothing links to
  // either until then, because a link to an unregistered route lands the admin
  // on the 404 page.
  ADMIN_EVENT_NEW: '/admin/events/new',
  ADMIN_EVENT_EDIT: '/admin/events/:eventId/edit',
  // Super Admin console: Attendee console (MYN-94 / MYN-95). A tab under the
  // Events section rather than its own sidebar entry, so it lives under the
  // events path. `events/attendees` cannot collide with `events/:eventId/edit`
  // (different depth) - and there is no bare `events/:eventId` route.
  ADMIN_ATTENDEES: '/admin/events/attendees',
  // Super Admin console: Applications (MYN-81).
  ADMIN_APPLICATIONS: '/admin/applications',
  /** Admin roles and permissions (MYN-308). */
  ADMIN_ROLES: '/admin/roles',
  // Super Admin console: Trusted Partner administration (FR-71, MYN-103).
  ADMIN_PARTNERS: '/admin/partners',
  // Super Admin console: one partner's profile rendered as its own members see
  // it, read-only (MYN-186). Same shape and rationale as
  // ADMIN_STARTUP_PROFILE below - a route rather than another drawer section,
  // because the point is the MEMBER page, and it opens in a new tab so the
  // console's table state survives.
  ADMIN_PARTNER_PROFILE: '/admin/partners/:entityId/profile',
  // Super Admin console: Trusted Partner special offers review (MYN-110).
  // Still its own console (it lists OFFERS across every partner, with its own
  // filters, sort and export), but reached as a path tab under the Partners
  // section rather than a separate sidebar entry. `partners/offers` cannot
  // collide with `partners/:entityId/profile` (different depth).
  // The Backend's offer-submitted notification `linkPath`
  // (ADMIN_PARTNER_OFFERS_LINK_PATH) must match this.
  ADMIN_PARTNER_OFFERS: '/admin/partners/offers',
  // Super Admin console: Investor administration (FR-52, MYN-129/130).
  ADMIN_INVESTORS: '/admin/investors',
  // Super Admin console: Startup administration. Added with MYN-129/130 for
  // `CREATED_ENTITY_CONSOLE_ROUTE`, which needs every entity type's console as
  // a value in one lookup. `admin-nav.ts` keeps building its own Startups entry
  // as `${ROUTES.ADMIN}/startups` - that was a deliberate per-file style
  // finding (MYN-127 Task 3), not a rule against this constant existing.
  ADMIN_STARTUPS: '/admin/startups',
  // Every SR NEXT Premium period across startups (2026-10-14), a section tab beside the Startups console.
  ADMIN_STARTUPS_PREMIUM: '/admin/startups/premium',
  /** Startup admin invites (MYN-311); must match the Backend's ADMIN_STARTUP_INVITES_LINK_PATH. */
  ADMIN_STARTUP_INVITES: '/admin/startups/invites',
  // Super Admin console: one startup's profile rendered as its own members see
  // it, read-only (sprint 3 feedback). A route rather than another drawer tab
  // because the point is the MEMBER page, chrome and layout included - and
  // because it opens in a new tab, leaving the console's table state untouched.
  ADMIN_STARTUP_PROFILE: '/admin/startups/:entityId/profile',
  // Super Admin console: native matching engine (MYN-168 to MYN-174, M10) -
  // run history/trigger and one run's results. The KPI dashboard (MYN-174)
  // is no longer its own route - its tiles render on this same screen,
  // above the run-history table (FR-126, MYN-205). No redirect from the
  // retired `/admin/matching/kpi`.
  ADMIN_MATCHING: '/admin/matching',
  ADMIN_MATCHING_RUN: '/admin/matching/runs/:runId',
  // Super Admin console: public page content manager (MYN-196, Backend
  // Backend/docs/specs/features/public-pages/02-design.md §18). One screen - a Pages grid with an inline
  // per-page editor, not a second routed screen (this repo's own
  // docs/specs/features/public-pages/02-design.md §16, REVISED 2026-09-23).
  ADMIN_PAGES: '/admin/pages',
  // Super Admin console: plan catalogue and promo codes (FR-40, MYN-112).
  // Separate consoles with separate listings, reached as path tabs under one
  // Plans sidebar entry. Promo codes sit under the plans path so NavLink's
  // prefix match keeps that entry active on either tab.
  ADMIN_PLANS: '/admin/plans',
  ADMIN_PROMO_CODES: '/admin/plans/promo-codes',
  // Super Admin console: financial report and the Stripe payments view
  // (FR-43, MYN-115 to MYN-117). Path tabs under one Financials entry, same
  // shape as Plans above.
  ADMIN_FINANCIALS: '/admin/financials',
  ADMIN_PAYMENTS: '/admin/financials/payments',
  // Public Shared Startup Profile view (MYN-41/58) - flat, deep-linked,
  // reachable cold with no session (ADR FE-1 "can they arrive cold?" test).
  SHARED_PROFILE: '/p/:token',
  SHARED_PROFILE_VERIFY_OTP: '/p/:token/verify-otp',
  SHARED_PROFILE_VIEW: '/p/:token/view',
} as const;

/**
 * Concrete path for `ADMIN_STARTUP_PROFILE`, so no call site builds the URL by
 * string-concatenating `/admin/startups/` itself.
 *
 * @param entityId - the startup entity being viewed
 */
export function adminStartupProfilePath(entityId: string): string {
  return ROUTES.ADMIN_STARTUP_PROFILE.replace(':entityId', entityId);
}

/**
 * Concrete path for `ADMIN_PARTNER_PROFILE`, so no call site builds the URL by
 * string-concatenating `/admin/partners/` itself.
 *
 * @param entityId - the trusted partner entity being viewed
 */
export function adminPartnerProfilePath(entityId: string): string {
  return ROUTES.ADMIN_PARTNER_PROFILE.replace(':entityId', entityId);
}

/**
 * Concrete path for `TRUSTED_PARTNER_DETAIL`, so no call site builds the URL by
 * string-concatenating `/trusted-partners/` itself.
 *
 * @param entityId - the trusted partner entity being viewed
 */
export function trustedPartnerDetailPath(entityId: string): string {
  return ROUTES.TRUSTED_PARTNER_DETAIL.replace(':entityId', entityId);
}

/**
 * Concrete path for `EVENT_DETAIL`, so no call site builds the URL by
 * string-concatenating `/events/` itself.
 *
 * @param id - the event UUID
 */
export function eventDetailPath(id: string): string {
  return ROUTES.EVENT_DETAIL.replace(':id', id);
}

/**
 * Concrete path for `ADMIN_EVENT_EDIT`, so no call site builds the URL by
 * string-concatenating `/admin/events/` itself - the same contract
 * `adminStartupProfilePath` above exists for.
 *
 * @param eventId - the event being edited
 */
export function adminEventEditPath(eventId: string): string {
  return ROUTES.ADMIN_EVENT_EDIT.replace(':eventId', eventId);
}

/**
 * Concrete path for `ADMIN_MATCHING_RUN`, so no call site builds the URL by
 * string-concatenating `/admin/matching/runs/` itself - same contract as
 * `adminEventEditPath`. Also what the matching-run-completion notification's
 * `linkPath` resolves to on the Backend.
 *
 * @param runId - the matching run being viewed
 */
export function adminMatchingRunPath(runId: string): string {
  return ROUTES.ADMIN_MATCHING_RUN.replace(':runId', runId);
}

/**
 * Concrete path for the admin attendee console, pre-filtered to one event via
 * its `eventId` query param. `AttendeesConsolePage` reads filters straight off
 * `useSearchParams()` through `useTableQuery` on render, so arriving at this
 * URL lands pre-filtered with zero extra wiring on the console side.
 *
 * @param eventId - the event to pre-filter the attendee console to
 */
export function adminAttendeesForEventPath(eventId: string): string {
  return `${ROUTES.ADMIN_ATTENDEES}?eventId=${eventId}`;
}

/** Query param that opens the Applications console's invite dialog on arrival. */
export const ADMIN_INVITE_QUERY_PARAM = 'invite';

/**
 * Applications console with the invite dialog open and preset to one type.
 * Other consoles link here rather than hosting the dialog themselves: the
 * invite creates an application, so the admin must land where it appears.
 *
 * @param type - the applicant type to preselect in the dialog
 */
export function adminApplicationsInvitePath(type: 'investor' | 'trusted_partner'): string {
  return `${ROUTES.ADMIN_APPLICATIONS}?${ADMIN_INVITE_QUERY_PARAM}=${type}`;
}

/**
 * Concrete path for `RESOURCE_FOLDER` so no call site builds the URL by
 * string-concatenating `/resources/folders/` itself.
 *
 * @param slug - the folder slug (stable, never changes on rename)
 */
export function resourceFolderPath(slug: string): string {
  return ROUTES.RESOURCE_FOLDER.replace(':slug', slug);
}

/** The query param of `BILLING_REVIEW` that names the chosen plan. */
export const BILLING_REVIEW_PLAN_PARAM = 'plan';

/**
 * The checkout review step with a plan preselected, so no call site builds the
 * `?plan=` query by hand.
 *
 * @param planId - the plan to review (URL-encoded here)
 */
export function billingReviewPath(planId: string): string {
  return `${ROUTES.BILLING_REVIEW}?${BILLING_REVIEW_PLAN_PARAM}=${encodeURIComponent(planId)}`;
}

/**
 * Alias of `eventDetailPath` — kept so existing callers (admin copy-link util)
 * do not need updating. All event links resolve through `EVENT_DETAIL`.
 *
 * @param id - the event UUID
 */
export function privateEventPath(id: string): string {
  return eventDetailPath(id);
}

const ENTITY_PROFILE_ROUTE: Record<EntityType, string> = {
  [ENTITY_TYPES.STARTUP]: ROUTES.STARTUP_PROFILE,
  [ENTITY_TYPES.INVESTOR]: ROUTES.INVESTOR_PROFILE,
  [ENTITY_TYPES.TRUSTED_PARTNER]: ROUTES.PARTNER_PROFILE,
};

/**
 * The "my profile" route for the current session: the persona-prefixed entity
 * profile when the user has an active membership, else the individual account
 * (`/me`). Used by the nav so one link points at the right owned page.
 */
export function profileRouteFor(membership: ActiveMembership | null): string {
  return membership ? ENTITY_PROFILE_ROUTE[membership.entityType] : ROUTES.ACCOUNT;
}
